import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useActivations from '../../Activations/components/hooks/useActivations';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import AppShell from '../chrome/AppShell';
import { Dialog, Field, TextInput } from '../components/forms';
import {
    Chipbar,
    MenuItem,
    MenuLabel,
    MenuSep,
    Segmented,
    TCard,
    TEmpty,
    TFoot,
    Toolbar,
    TRow,
    TSkeleton,
} from '../components/table';
import { PageHeadActions, ViewSwitch } from '../components/nav';
import TwoWeekCalendar, { CalEvent, CalLegend, addDays, dayStart, endDayOf, mondayOf } from '../components/calendar';
import { Board, BoardCard } from '../components/board';
import { ProdRow, isLiveStats, prodToEvent, useEventDetails, useProductionRange, useView } from '../lib/schedule';
import { STAGE, dateRangeLabel } from '../lib/jobs';
import { SubscribeButton } from '../components/feeds';
import '../calendar.css';

/*
 * Activations — the table archetype's first instantiation
 * (docs/design-brief/table-concept.html; PRD "Activations").
 * Rules carried here: forward-looking periods (default This week);
 * search spans all time, newest-first, archived included, and ignores
 * the date scope; archived otherwise appear only under All time; one
 * date per row (proximity inside a week, month-day beyond, year when
 * not this year); honest footer counts.
 *
 * Status column: the list endpoint carries no device/experience data
 * yet, so it says "Wrapped" for past events and stays quiet otherwise
 * (empty ≠ failed) — rule-11 readiness lands with its backend.
 */

interface ActivationRow {
    id: number;
    name: string;
    location?: string;
    date?: string;
    isArchived?: boolean;
    jobId?: number;
}

/* Calendar and Board are view-only; both read GET /production/activations by date. */
const VIEWS = ['list', 'calendar', 'board'] as const;
type View = (typeof VIEWS)[number];
const CAL_MODES = ['month', 'week'] as const;
const BOARD_COLS: Array<{ key: string; label: string; statuses: string[] }> = [
    { key: 'pending', label: 'Pending deal', statuses: ['pending'] },
    { key: 'ready_for_production', label: 'Ready for production', statuses: ['ready_for_production'] },
    { key: 'in_production', label: 'In production', statuses: ['in_production'] },
    { key: 'show_ready', label: 'Show ready', statuses: ['show_ready'] },
    { key: 'wrapped', label: 'Wrapped', statuses: ['past', 'wrapped'] },
];

type PeriodKey = 'today' | 'tomorrow' | 'week' | '30d' | '12mo' | 'all';

const PERIODS: Array<{ key: PeriodKey; label: string; days?: number }> = [
    { key: 'today', label: 'Today', days: 1 },
    { key: 'tomorrow', label: 'Tomorrow', days: 2 },
    { key: 'week', label: 'This week', days: 7 },
    { key: '30d', label: 'Next 30 days', days: 30 },
    { key: '12mo', label: 'Next 12 months', days: 365 },
    { key: 'all', label: 'All time' },
];

/* Common periods shown inline; the rest stay in the filter menu. */
const QUICK_PERIODS: Array<{ value: PeriodKey; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This week' },
    { value: '30d', label: '30 days' },
    { value: 'all', label: 'All' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfToday(): number {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

function parseDate(value?: string): number | null {
    if (!value) return null;
    /* activation dates are stored as midnight UTC: read them as that calendar
       day, not the evening before (which US time zones would show) */
    const utcDay = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?(?:Z|\+00:00)$/.exec(value);
    if (utcDay) return new Date(+utcDay[1], +utcDay[2] - 1, +utcDay[3]).getTime();
    const d = new Date(value);
    const t = d.getTime();
    if (Number.isNaN(t)) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** One date per row: proximity inside a week, month-day beyond, year when
    it isn't this year. */
function dateLabel(t: number | null): string {
    if (t === null) return '';
    const today = startOfToday();
    const diff = Math.round((t - today) / DAY_MS);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    const d = new Date(t);
    if (diff > 1 && diff < 7) {
        return d.toLocaleDateString(undefined, { weekday: 'long' });
    }
    const sameYear = d.getFullYear() === new Date().getFullYear();
    const md = d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    });
    return sameYear ? md : `${md} ’${String(d.getFullYear()).slice(-2)}`;
}

export default function ActivationsV3() {
    const navigate = useNavigate();
    const { getActivations } = useActivations();
    const authenticatedApi = useAuthenticatedApi();

    const [rows, setRows] = useState<ActivationRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [period, setPeriod] = useState<PeriodKey>('week');
    const [location, setLocation] = useState<string | null>(null);
    const [sort, setSort] = useState<'date' | 'name'>('date');
    const [view, setView] = useView<View>('activations', VIEWS, 'list');
    const [calMode, setCalMode] = useView<(typeof CAL_MODES)[number]>('activations.cal', CAL_MODES, 'month');
    const [anchor, setAnchor] = useState(() => startOfToday());

    const load = useCallback(() => {
        getActivations()
            .then((list: ActivationRow[]) => {
                setRows(Array.isArray(list) ? list : []);
                setFailed(false);
            })
            .catch(() => setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(load, [load]);

    const q = query.trim().toLowerCase();
    const searching = q !== '';
    const all = useMemo(() => rows ?? [], [rows]);

    const locations = useMemo(() => {
        const counts = new Map<string, number>();
        all.forEach((r) => {
            const value = (r.location ?? '').trim();
            if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
        });
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([value]) => value);
    }, [all]);

    const visible = useMemo(() => {
        let list: ActivationRow[];
        if (searching) {
            /* all time, archived included, newest first (PRD) */
            list = all.filter((r) =>
                (r.name ?? '').toLowerCase().includes(q),
            );
            list.sort(
                (a, b) => (parseDate(b.date) ?? 0) - (parseDate(a.date) ?? 0),
            );
        } else {
            const today = startOfToday();
            const def = PERIODS.find((p) => p.key === period)!;
            list = all.filter((r) => {
                if (r.isArchived && period !== 'all') return false;
                if (period === 'all') return true;
                const t = parseDate(r.date);
                if (t === null) return false;
                const from = period === 'tomorrow' ? today + DAY_MS : today;
                return t >= from && t < today + def.days! * DAY_MS;
            });
            if (sort === 'name') {
                list.sort((a, b) =>
                    (a.name ?? '').localeCompare(b.name ?? ''),
                );
            } else if (period === 'all') {
                list.sort(
                    (a, b) =>
                        (parseDate(b.date) ?? 0) - (parseDate(a.date) ?? 0),
                );
            } else {
                list.sort(
                    (a, b) =>
                        (parseDate(a.date) ?? 0) - (parseDate(b.date) ?? 0),
                );
            }
        }
        if (location) {
            list = list.filter(
                (r) => (r.location ?? '').trim() === location,
            );
        }
        return list;
    }, [all, q, searching, period, sort, location]);

    const periodLabel = PERIODS.find((p) => p.key === period)!.label;

    /* Calendar: a month (Monday-first weeks) or a single week. */
    const today = startOfToday();
    const anchorDate = new Date(anchor);
    const monthFirst = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1).getTime();
    const monthDays = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 0).getDate();
    const calStart = calMode === 'month' ? mondayOf(monthFirst) : mondayOf(anchor);
    const calWeeks =
        calMode === 'month' ? Math.ceil((Math.round((monthFirst - calStart) / DAY_MS) + monthDays) / 7) : 1;
    const calEnd = addDays(calStart, calWeeks * 7 - 1);
    const calLabel =
        calMode === 'month'
            ? anchorDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
            : `${new Date(calStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${new Date(calEnd).toLocaleDateString(undefined, { month: new Date(calStart).getMonth() === new Date(calEnd).getMonth() ? undefined : 'short', day: 'numeric' })}`;
    const shiftCal = (n: number) =>
        setAnchor((a) => {
            const d = new Date(a);
            return calMode === 'month'
                ? new Date(d.getFullYear(), d.getMonth() + n, 1).getTime()
                : addDays(a, 7 * n);
        });
    /* the API filters by start date: look back a month so long events already underway still show */
    const cal = useProductionRange(addDays(calStart, -31), calEnd, view === 'calendar');

    /* Board uses the list's period. */
    const def = PERIODS.find((p) => p.key === period)!;
    const boardFrom = period === 'all' ? null : period === 'tomorrow' ? addDays(today, 1) : today;
    const boardTo = period === 'all' ? null : addDays(today, def.days! - 1);
    const board = useProductionRange(boardFrom, boardTo, view === 'board');

    const actById = useMemo(() => new Map(all.map((r) => [r.id, r])), [all]);
    const placeOf = (r: ProdRow) => (actById.get(r.activationId)?.location ?? r.venueName ?? '').trim();
    const keep = (r: ProdRow) =>
        !r.isArchived &&
        !actById.get(r.activationId)?.isArchived &&
        (!q || r.name.toLowerCase().includes(q)) &&
        (!location || placeOf(r) === location);
    const calRows = useMemo(
        () => (cal.rows ?? []).filter(keep),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cal.rows, q, location, actById],
    );
    const boardRows = useMemo(
        () => (board.rows ?? []).filter((r) => keep(r) && r.status !== 'lost'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [board.rows, q, location, actById],
    );
    const upcomingIds = calRows
        .filter((r) => r.startAt && dayStart(r.endAt ?? r.startAt) >= today)
        .map((r) => r.activationId);
    const readiness = useEventDetails(upcomingIds, ['devices', 'setup']);
    const todayIds = [...calRows, ...boardRows]
        .filter((r) => r.startAt && dayStart(r.startAt) <= today && dayStart(r.endAt ?? r.startAt) >= today)
        .map((r) => r.activationId);
    const liveInfo = useEventDetails(todayIds, ['stats']);
    const calEvents = calRows
        .map((r) =>
            prodToEvent(r, { ...readiness[r.activationId], stats: liveInfo[r.activationId]?.stats }, placeOf(r) || undefined),
        )
        .filter((e): e is CalEvent => e !== null);
    const inView = calEvents.filter((e) => e.endDay >= (calMode === 'month' ? monthFirst : calStart) && e.startDay <= (calMode === 'month' ? addDays(monthFirst, monthDays - 1) : calEnd));
    const needAttention = inView.filter((e) => e.status === 'attention').length;

    /* Clone keeps the source's date and job. */
    const [cloneFrom, setCloneFrom] = useState<ActivationRow | null>(null);
    const [cloneName, setCloneName] = useState('');
    const [cloning, setCloning] = useState(false);
    const [cloneError, setCloneError] = useState('');
    const openClone = (row: ActivationRow) => {
        setCloneFrom(row);
        setCloneName(`${row.name} copy`);
        setCloneError('');
    };
    const closeClone = useCallback(() => setCloneFrom(null), []);
    const clone = async () => {
        const row = cloneFrom;
        const name = cloneName.trim();
        if (!row || !name || cloning) return;
        setCloning(true);
        try {
            const response = await authenticatedApi.post(
                `/activations/clone/${row.id}`,
                JSON.stringify({
                    name,
                    date: row.date,
                    jobId: row.jobId ?? null,
                }),
            );
            const created = response?.data;
            setCloneFrom(null);
            if (created?.id) navigate(`/activations/${created.id}`);
            else load();
        } catch {
            setCloneError('Couldn’t clone this activation. Try again.');
        } finally {
            setCloning(false);
        }
    };

    /* Archive applies immediately with Undo instead of a confirm. */
    const [toast, setToast] = useState<{
        text: string;
        undo?: () => void;
    } | null>(null);
    useEffect(() => {
        if (!toast) return;
        const t = window.setTimeout(() => setToast(null), 5000);
        return () => window.clearTimeout(t);
    }, [toast]);
    const setArchived = async (
        row: ActivationRow,
        archived: boolean,
        offerUndo = true,
    ) => {
        const apply = (value: boolean) =>
            setRows((list) =>
                (list ?? []).map((r) =>
                    r.id === row.id ? { ...r, isArchived: value } : r,
                ),
            );
        apply(archived);
        setToast(
            offerUndo
                ? {
                      text: `${archived ? 'Archived' : 'Unarchived'} “${row.name}”`,
                      undo: () => {
                          setToast(null);
                          setArchived(row, !archived, false);
                      },
                  }
                : null,
        );
        try {
            await authenticatedApi.put(
                `/activations/${row.id}/${archived ? 'archive' : 'unarchive'}`,
            );
        } catch {
            apply(!archived);
            setToast({
                text: `Couldn’t ${archived ? 'archive' : 'unarchive'} “${row.name}”.`,
            });
        }
    };

    const sub = searching
        ? `${periodLabel} · ${all.length} events`
        : `${periodLabel}${location ? ' · filtered' : ''} · ${visible.length} events`;

    const foot = searching
        ? `${visible.length} results · all time · newest first`
        : `${visible.length} of ${all.length} · ${periodLabel}${location ? ' · 1 filter' : ''}`;

    return (
        <AppShell>
            <PageHeadActions
                title="Activations"
                sub={
                    view === 'calendar'
                        ? `${calLabel}${cal.rows ? ` · ${inView.length} events` : ''}`
                        : view === 'board'
                          ? `${periodLabel}${board.rows ? ` · ${boardRows.length} events` : ''}`
                          : rows === null
                            ? periodLabel
                            : sub
                }
                actions={
                    <ViewSwitch
                        value={view}
                        onChange={setView}
                        options={[
                            { value: 'list', label: 'List' },
                            { value: 'calendar', label: 'Calendar' },
                            { value: 'board', label: 'Board' },
                        ]}
                    />
                }
            />
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search Activations"
                filterCount={location ? 1 : 0}
                primaryLabel="New activation"
                onPrimary={() => navigate('/activations/create')}
                inline={
                    view === 'calendar' ? (
                        <Segmented
                            label="Calendar range"
                            options={[
                                { value: 'month', label: 'Month' },
                                { value: 'week', label: 'Week' },
                            ]}
                            value={calMode}
                            onChange={setCalMode}
                        />
                    ) : (
                        (!searching || view === 'board') && (
                            <Segmented
                                label="Period"
                                options={QUICK_PERIODS}
                                value={period}
                                onChange={setPeriod}
                            />
                        )
                    )
                }
                menu={(close) => (
                    <>
                        {view !== 'calendar' && (
                        <>
                        <MenuLabel>Date</MenuLabel>
                        {PERIODS.map((p) => (
                            <MenuItem
                                key={p.key}
                                on={p.key === period}
                                onClick={() => {
                                    setPeriod(p.key);
                                    close();
                                }}
                            >
                                {p.label}
                            </MenuItem>
                        ))}
                        </>
                        )}
                        {locations.length > 0 && (
                            <>
                                {view !== 'calendar' && <MenuSep />}
                                <MenuLabel>Location</MenuLabel>
                                {locations.map((value) => (
                                    <MenuItem
                                        key={value}
                                        on={location === value}
                                        onClick={() => {
                                            setLocation(
                                                location === value
                                                    ? null
                                                    : value,
                                            );
                                            close();
                                        }}
                                    >
                                        {value}
                                    </MenuItem>
                                ))}
                            </>
                        )}
                        {view === 'list' && (
                        <>
                        <MenuSep />
                        <MenuLabel>Sort by</MenuLabel>
                        <MenuItem
                            on={sort === 'date'}
                            onClick={() => {
                                setSort('date');
                                close();
                            }}
                        >
                            Date
                        </MenuItem>
                        <MenuItem
                            on={sort === 'name'}
                            onClick={() => {
                                setSort('name');
                                close();
                            }}
                        >
                            Name
                        </MenuItem>
                        </>
                        )}
                    </>
                )}
            />
            <Chipbar
                chips={
                    location
                        ? [
                              {
                                  label: 'Location',
                                  value: location,
                                  onClear: () => setLocation(null),
                              },
                          ]
                        : []
                }
            />
            {view === 'calendar' && (
                <>
                    {cal.failed ? (
                        <TCard>
                            <TEmpty message="Couldn’t load the calendar." actionLabel="Retry" onAction={cal.reload} />
                        </TCard>
                    ) : (
                        <>
                            <TwoWeekCalendar
                                events={calEvents}
                                start={calStart}
                                weeks={calWeeks}
                                lanes={calMode === 'month' ? 3 : 8}
                                tall={calMode === 'week'}
                                month={calMode === 'month' ? anchorDate.getMonth() : undefined}
                                title=""
                                range={calLabel}
                                unit={calMode}
                                isCurrent={calMode === 'month' ? monthFirst === new Date(new Date(today).getFullYear(), new Date(today).getMonth(), 1).getTime() : calStart === mondayOf(today)}
                                onShift={shiftCal}
                                onToday={() => setAnchor(today)}
                                navigate={navigate}
                                toolbar={<SubscribeButton />}
                            />
                            <CalLegend />
                            {cal.rows && (
                                <TFoot>
                                    {inView.length} events · {calLabel}
                                    {needAttention ? ` · ${needAttention} need attention` : ''}
                                    {location || q ? ' · filtered' : ''}
                                </TFoot>
                            )}
                        </>
                    )}
                </>
            )}
            {view === 'board' && (
                <>
                    {board.failed ? (
                        <TCard>
                            <TEmpty message="Couldn’t load the board." actionLabel="Retry" onAction={board.reload} />
                        </TCard>
                    ) : board.rows === null ? (
                        <TCard>
                            <TSkeleton rows={3} />
                        </TCard>
                    ) : (
                        <>
                            <Board
                                label="Activations by production stage"
                                columns={BOARD_COLS.map((c) => {
                                    const list = boardRows.filter((r) => c.statuses.includes(r.status ?? ''));
                                    return {
                                        key: c.key,
                                        label: c.label,
                                        tone: STAGE[c.statuses[0]]?.tone,
                                        count: list.length,
                                        empty: c.key === 'wrapped' && period !== 'all' ? 'Past events show under All' : 'Nothing here',
                                        children: list.map((r) => {
                                            const past = r.status === 'past' || r.status === 'wrapped';
                                            const live = isLiveStats(liveInfo[r.activationId]?.stats);
                                            const place = placeOf(r);
                                            return (
                                                <BoardCard
                                                    key={r.activationId}
                                                    href={r.isProvisional ? undefined : `/activations/${r.activationId}`}
                                                    onOpen={r.jobId ? () => navigate(`/pipeline/${r.jobId}`) : undefined}
                                                    navigate={navigate}
                                                    name={r.name}
                                                    meta={[dateRangeLabel(r.startAt, r.startAt && r.endAt ? new Date(endDayOf(r.endAt, dayStart(r.startAt))).toISOString() : r.endAt), place].filter(Boolean).join(' · ')}
                                                    foot={
                                                        <>
                                                            {live && <span className="live">● Live</span>}
                                                            {!past &&
                                                                (r.staffCount ? (
                                                                    <span>{r.staffCount} crew</span>
                                                                ) : (
                                                                    <span className="warn">No crew yet</span>
                                                                ))}
                                                            {r.jobName && r.jobName !== r.name && <span>· {r.jobName}</span>}
                                                        </>
                                                    }
                                                />
                                            );
                                        }),
                                    };
                                })}
                            />
                            <TFoot>
                                {boardRows.length} events · {periodLabel}
                                {location || q ? ' · filtered' : ''} · view only — stages change on the event
                            </TFoot>
                        </>
                    )}
                </>
            )}
            {view === 'list' && failed && (
                <TCard>
                    <TEmpty
                        message="Couldn’t load activations."
                        actionLabel="Retry"
                        onAction={load}
                    />
                </TCard>
            )}
            {view === 'list' && !failed && rows !== null && visible.length === 0 && (
                <TCard>
                <TEmpty
                    message={
                        searching
                            ? `Nothing matches “${query}”.`
                            : `Nothing ${periodLabel === 'All time' ? 'yet' : periodLabel.toLowerCase()}.`
                    }
                    actionLabel={
                        !searching && period !== 'all'
                            ? period === '30d' || period === '12mo'
                                ? 'Show all time'
                                : 'Show next 30 days'
                            : undefined
                    }
                    onAction={() =>
                        setPeriod(
                            period === '30d' || period === '12mo'
                                ? 'all'
                                : '30d',
                        )
                    }
                />
                </TCard>
            )}
            {view === 'list' && !failed && rows === null && (
                <TCard>
                    <TSkeleton />
                </TCard>
            )}
            {view === 'list' && !failed && visible.length > 0 && (
                <TCard
                    settle
                    key={`${searching}|${period}|${location}|${sort}`}
                >
                    {visible.map((row, index) => {
                        const t = parseDate(row.date);
                        const wrapped =
                            t !== null && t < startOfToday() ? 'Wrapped' : '';
                        return (
                            <TRow
                                key={row.id}
                                index={index}
                                name={row.name}
                                badge={row.isArchived ? 'Archived' : undefined}
                                meta={(row.location ?? '').trim()}
                                metaDate={dateLabel(t)}
                                date={dateLabel(t)}
                                status={wrapped}
                                href={`/activations/${row.id}`}
                                onOpen={() =>
                                    navigate(`/activations/${row.id}`)
                                }
                                menuItems={[
                                    {
                                        label: 'Open',
                                        onClick: () =>
                                            navigate(
                                                `/activations/${row.id}`,
                                            ),
                                    },
                                    {
                                        label: 'Clone…',
                                        onClick: () => openClone(row),
                                    },
                                    row.isArchived
                                        ? {
                                              label: 'Unarchive',
                                              onClick: () =>
                                                  setArchived(row, false),
                                          }
                                        : {
                                              label: 'Archive',
                                              onClick: () =>
                                                  setArchived(row, true),
                                          },
                                ]}
                            />
                        );
                    })}
                </TCard>
            )}
            {view === 'list' && !failed && rows !== null && <TFoot>{foot}</TFoot>}
            <Dialog
                open={cloneFrom !== null}
                title="Clone activation"
                onClose={closeClone}
                onSubmit={clone}
                footer={
                    <>
                        <button
                            type="button"
                            className="tb-btn"
                            onClick={closeClone}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="tb-new"
                            disabled={!cloneName.trim() || cloning}
                        >
                            {cloning ? 'Cloning…' : 'Clone'}
                        </button>
                    </>
                }
            >
                <Field
                    label="Name"
                    help="Keeps the same date and job."
                    error={cloneError || undefined}
                >
                    <TextInput
                        autoFocus
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </Field>
            </Dialog>
            {toast && (
                <div className="toasthost" role="status" aria-live="polite">
                    <div className="toast">
                        <span className="t1" style={{ flex: 1, minWidth: 0 }}>
                            {toast.text}
                        </span>
                        {toast.undo && (
                            <button
                                type="button"
                                className="tb-btn"
                                onClick={toast.undo}
                            >
                                Undo
                            </button>
                        )}
                    </div>
                </div>
            )}
        </AppShell>
    );
}
